import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessFlowchartUIOnlyNodeType} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import type {
  ProcessFlowchartStaticNode,
  ProcessFlowchartStaticEdge,
  ProcessStaticFlowchart,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import type {CollapsedViewerNodeData} from 'processes/graph-builder-processes/flowchart-viz/ReadOnlyFlowchartTypes';
import collapseNodesWithNoTasksInFlowchart from 'processes/graph-builder-processes/flowchart-viz/util/collapseNodesWithNoTasksInFlowchart';
import {getClientIdForProcessFlowchartEdge} from 'processes/graph-builder-processes/flowchart-viz/util/ConvertProcessFlowchartForReactFlowUtil';
import {
  getEdgeHash,
  getPathsWithOnlyRouters,
} from 'processes/graph-builder-processes/flowchart-viz/util/mergeTaskGroupLineageIntoFlowchart';

import {
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  ProcessFlowchartNodeType,
} from '../processFlowchartConfigEditorConstants';

interface CollapsibleNode {
  id: string;
  nodeName: string;
  nodeDetailsType: ProcessFlowchartNodeDetailsType | string;
  statusCounts?: Array<{statusType: string; displayName: string; count: number}>;
}

export interface CollapsibleFlowchart<N extends CollapsibleNode = CollapsibleNode> {
  nodesStaticData: Array<N>;
  edgesStaticData: Array<FlowchartStaticEdgeData>;
}

interface CollapsedResult<N extends CollapsibleNode> {
  nodesStaticData: Array<N | CollapsedViewerNodeData>;
  edgesStaticData: Array<FlowchartStaticEdgeData>;
}

function toOldModel(
  flowchart: CollapsibleFlowchart,
  lineageEdgeHashes?: ReadonlySet<string>
): ProcessStaticFlowchart {
  const oldModel: ProcessStaticFlowchart = {
    clientId: 'collapse-adapter',
    nodes: flowchart.nodesStaticData.map(
      (n) =>
        ({
          clientId: n.id,
          name: n.nodeName,
          type: n.nodeDetailsType ?? '',
          instanceData: {},
          statusCounts: n.statusCounts?.map((sc) => ({statusType: sc.statusType, count: sc.count})),
        }) as ProcessFlowchartStaticNode
    ),
    edges: flowchart.edgesStaticData.map((e) => ({
      clientId: e.id,
      fromNodeClientId: e.source,
      toNodeClientId: e.target,
    })),
  };

  if (lineageEdgeHashes != null) {
    // Pass 1: mark edges whose source->target hash appears in lineage data
    for (const edge of oldModel.edges) {
      edge.hasBeenUsedToCreateData = lineageEdgeHashes.has(
        getEdgeHash(edge.fromNodeClientId, edge.toNodeClientId)
      );
    }
    // Pass 2: propagate through router-only paths (same logic as the old editor)
    const routerPaths = getPathsWithOnlyRouters(oldModel);
    for (const {fromNodeId, toNodeId, relevantEdgeIds} of routerPaths) {
      if (lineageEdgeHashes.has(getEdgeHash(fromNodeId, toNodeId))) {
        for (const edgeId of relevantEdgeIds) {
          const edge = oldModel.edges.find((e) => e.clientId === edgeId);
          if (edge != null) {
            edge.hasBeenUsedToCreateData = true;
          }
        }
      }
    }
  }

  return oldModel;
}

/**
 * The core collapse algorithm misses incoming edges when multiple paths merge into a
 * collapsed node. E.g. if B, C, D all flow into Target(empty) → Outputs, only whichever
 * source is processed first gets an edge to the collapsed node. This adds the missing ones.
 */
function addMissingMergeEdges(
  original: ProcessStaticFlowchart,
  collapsed: ProcessStaticFlowchart
): ProcessStaticFlowchart {
  const collapsedNodeIds = new Set(collapsed.nodes.map((n) => n.clientId));
  const survivingNodeIds = new Set(
    collapsed.nodes
      .filter((n) => n.type !== ProcessFlowchartUIOnlyNodeType.COLLAPSED_NODE)
      .map((n) => n.clientId)
  );
  const removedNodeIds = new Set(
    original.nodes.filter((n) => !collapsedNodeIds.has(n.clientId)).map((n) => n.clientId)
  );

  if (removedNodeIds.size === 0) {
    return collapsed;
  }

  // Build original graph forward adjacency
  const originalOutgoing = new Map<string, Array<string>>();
  for (const edge of original.edges) {
    if (!originalOutgoing.has(edge.fromNodeClientId)) {
      originalOutgoing.set(edge.fromNodeClientId, []);
    }
    originalOutgoing.get(edge.fromNodeClientId)!.push(edge.toNodeClientId);
  }

  // Map each removed node to the collapsed node that absorbed it by BFS-ing from
  // each collapsed node's predecessors through removed nodes in the original graph.
  const removedToCollapsed = new Map<string, string>();
  const collapsedNodesList = collapsed.nodes.filter(
    (n) => n.type === ProcessFlowchartUIOnlyNodeType.COLLAPSED_NODE
  );

  for (const cNode of collapsedNodesList) {
    const predecessors = collapsed.edges
      .filter((e) => e.toNodeClientId === cNode.clientId)
      .map((e) => e.fromNodeClientId);

    for (const pred of predecessors) {
      for (const target of originalOutgoing.get(pred) ?? []) {
        if (!removedNodeIds.has(target) || removedToCollapsed.has(target)) {
          continue;
        }
        const queue = [target];
        const visited = new Set<string>();
        while (queue.length > 0) {
          const current = queue.shift()!;
          if (visited.has(current)) {
            continue;
          }
          visited.add(current);
          removedToCollapsed.set(current, cNode.clientId);
          for (const succ of originalOutgoing.get(current) ?? []) {
            if (removedNodeIds.has(succ) && !visited.has(succ) && !removedToCollapsed.has(succ)) {
              queue.push(succ);
            }
          }
        }
      }
    }
  }

  const additionalEdges: Array<ProcessFlowchartStaticEdge> = [];
  const existingEdgePairs = new Set(collapsed.edges.map((e) => `${e.fromNodeClientId}->${e.toNodeClientId}`));

  for (const edge of original.edges) {
    if (!survivingNodeIds.has(edge.fromNodeClientId) || !removedNodeIds.has(edge.toNodeClientId)) {
      continue;
    }
    const collapsedNodeId = removedToCollapsed.get(edge.toNodeClientId);
    if (collapsedNodeId == null) {
      continue;
    }
    const pair = `${edge.fromNodeClientId}->${collapsedNodeId}`;
    if (existingEdgePairs.has(pair)) {
      continue;
    }
    existingEdgePairs.add(pair);
    additionalEdges.push({
      clientId: getClientIdForProcessFlowchartEdge(edge.fromNodeClientId, collapsedNodeId),
      fromNodeClientId: edge.fromNodeClientId,
      toNodeClientId: collapsedNodeId,
    });
  }

  if (additionalEdges.length === 0) {
    return collapsed;
  }

  return {
    ...collapsed,
    edges: [...collapsed.edges, ...additionalEdges],
  };
}

function mapCollapsedNodes<N extends CollapsibleNode>(
  collapsedNodes: Array<ProcessFlowchartStaticNode>,
  originalNodeById: Map<string, N>
): Array<N | CollapsedViewerNodeData> {
  const result: Array<N | CollapsedViewerNodeData> = [];
  for (const oldNode of collapsedNodes) {
    const original = originalNodeById.get(oldNode.clientId);
    if (original != null) {
      result.push(original);
    } else if (oldNode.type === ProcessFlowchartUIOnlyNodeType.COLLAPSED_NODE) {
      result.push({
        id: oldNode.clientId,
        nodeType: ProcessFlowchartNodeType.COLLAPSED_NODE,
        nodeDetailsType: ProcessFlowchartNodeType.COLLAPSED_NODE,
        nodeName: oldNode.name,
        numNodesRepresented: oldNode.numNodesRepresented ?? 0,
      });
    }
  }
  return result;
}

function mapCollapsedEdges(
  collapsedFlowchart: ProcessStaticFlowchart,
  originalEdges: Array<FlowchartStaticEdgeData>,
  resultNodeIds: ReadonlySet<string>
): Array<FlowchartStaticEdgeData> {
  const edgesBySource = new Map<string, FlowchartStaticEdgeData>();
  const edgesByTarget = new Map<string, FlowchartStaticEdgeData>();
  const edgesByPair = new Map<string, FlowchartStaticEdgeData>();
  for (const edge of originalEdges) {
    edgesByPair.set(`${edge.source}->${edge.target}`, edge);
    if (!edgesBySource.has(edge.source)) {
      edgesBySource.set(edge.source, edge);
    }
    if (!edgesByTarget.has(edge.target)) {
      edgesByTarget.set(edge.target, edge);
    }
  }

  const result: Array<FlowchartStaticEdgeData> = [];
  const seenPairs = new Set<string>();
  for (const oldEdge of collapsedFlowchart.edges) {
    const {fromNodeClientId: source, toNodeClientId: target} = oldEdge;
    if (!resultNodeIds.has(source) || !resultNodeIds.has(target)) {
      continue;
    }
    const pairKey = `${source}->${target}`;
    if (seenPairs.has(pairKey)) {
      continue;
    }
    seenPairs.add(pairKey);
    const existing = edgesByPair.get(pairKey);
    if (existing != null) {
      result.push(existing);
    } else {
      const fromSource = edgesBySource.get(source);
      const toTarget = edgesByTarget.get(target);
      result.push({
        id: oldEdge.clientId,
        source,
        target,
        sourceHandle: fromSource?.sourceHandle ?? `${source}-source`,
        targetHandle: toTarget?.targetHandle ?? `${target}-target`,
        type: fromSource?.type ?? toTarget?.type ?? ProcessFlowchartEdgeType.CONTROL_FLOW,
      });
    }
  }
  return result;
}

/**
 * Collapses nodes without task data by delegating to
 * {@link collapseNodesWithNoTasksInFlowchart} via a data-model adapter.
 * Returns the original flowchart reference if nothing was collapsed.
 *
 * @param lineageEdgeHashes Edge hashes from {@link getTaskGroupLineageSummary} that indicate
 *   which task-to-task edges have been traversed by real execution. When provided, router edges
 *   are marked as used only when lineage data proves data flowed through them (matching the old
 *   editor behavior). When omitted, routers are never treated as having data.
 */
export default function collapseEmptyExecutionNodes<N extends CollapsibleNode>(
  flowchart: CollapsibleFlowchart<N>,
  lineageEdgeHashes?: ReadonlySet<string>
): CollapsibleFlowchart<N> | CollapsedResult<N> {
  const {nodesStaticData, edgesStaticData} = flowchart;

  if (nodesStaticData.length === 0) {
    return flowchart;
  }

  const oldModel = toOldModel(flowchart, lineageEdgeHashes);
  const collapsed = addMissingMergeEdges(oldModel, collapseNodesWithNoTasksInFlowchart(oldModel));
  const originalNodeById = new Map(nodesStaticData.map((n) => [n.id, n]));
  const resultNodes = mapCollapsedNodes(collapsed.nodes, originalNodeById);

  if (resultNodes.filter((n) => originalNodeById.has(n.id)).length === nodesStaticData.length) {
    return flowchart;
  }

  const resultNodeIds = new Set(resultNodes.map((n) => n.id));
  const resultEdges = mapCollapsedEdges(collapsed, edgesStaticData, resultNodeIds);

  return {nodesStaticData: resultNodes, edgesStaticData: resultEdges};
}
